// Selection Control(Checkbox / Radio / Switch) accent 색 토큰
//
// 디자인 가이드의 "Selection Controls" 페이지가 정의하는 파랑 3종은 **한 색에서 파생된 값**이다.
// 손으로 적은 팔레트가 아니라 흰색과 섞은 결과다 — 채널별로 역산해서 확인했다.
//
//   #5C9DDE (checked hover 바닥) = #0066CC 64% + 흰색 36%   (0.639 / 0.641 / 0.647)
//   #C2DAF3 (hover halo)         = #0066CC 24% + 흰색 76%   (0.239 / 0.242 / 0.235)
//
// 그래서 accent 를 지원할 때 3개 색을 받을 필요가 없다. **base 하나만 받으면 된다.**
// 기본 파랑도 하드코딩을 버리고 이 함수로 계산하므로 진실의 출처가 하나다.
//
// ⚠️ 레포에 색 파생 선례가 없다 (`alpha` 는 회색 2곳, `lighten`/`darken` 0곳).
//    이 파일이 최초다. 다른 토큰 파일과 형태는 맞췄다 — `theme/buttonTokens.ts` 참고.
//
// Checkbox 뿐 아니라 `@IntranetLibrary/Radio/dsRadio.tsx` 와 `Switch/index.tsx` 가
// 같은 파랑 3종을 하드코딩 중이다. 그래서 컴포넌트 폴더가 아니라 여기에 둔다
// (컴포넌트 폴더에 두면 Radio → Checkbox 역참조가 생긴다).

export type SelectionAccentTokens = {
  /** checked 바닥. 입력 base 그대로 */
  base: string;
  /** checked + hover/focus 바닥 */
  hover: string;
  /** hover/focus halo (`::before`) */
  halo: string;
};

/** 가이드 값(#5C9DDE / #C2DAF3)을 역산해 얻은 혼합 비율 */
const HOVER_MIX_RATIO = 0.64;
const HALO_MIX_RATIO = 0.24;

/** DS 기본 accent — 가이드의 Selected 색 */
const DEFAULT_BASE = '#0066CC';

/** `#RGB` / `#RRGGBB` → [r, g, b]. 형식이 아니면 null */
function parseHex(hex: string): [number, number, number] | null {
  const raw = hex.trim().replace(/^#/, '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

const toHex = (channel: number) =>
  channel.toString(16).padStart(2, '0').toUpperCase();

/**
 * hex 를 흰색과 섞는다. `ratio` 는 **원본 색의 비중**이다 (1 이면 원본 그대로).
 *
 * ⚠️ 반올림은 반드시 `Math.round` 다. `floor` 로 하면 `#5C9DDE` 가 `#5B9DDE` 로,
 * `#C2DAF3` 가 `#C1DAF2` 로 어긋나 **기존 파랑이 미묘하게 바뀐다.**
 * 이 함수가 가이드 값을 비트 단위로 재현하는 것이 기존 호출부 무영향의 근거다.
 *
 * 형식이 잘못된 입력은 원본을 그대로 돌려준다 — 렌더 중에 던지지 않는다.
 */
export function mixWithWhite(hex: string, ratio: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;

  return `#${rgb
    .map((channel) => toHex(Math.round(channel * ratio + 255 * (1 - ratio))))
    .join('')}`;
}

/**
 * hex 에 알파를 입혀 `rgba()` 로 만든다.
 *
 * ⚠️ `mixWithWhite` 와 바꿔 쓰면 안 된다. 흰색 혼합은 **바닥이 흰색일 때만** 같은 결과를 낸다 —
 * 캘린더 주말 셀(`#F9FAFB`)이나 회색 패널 위에서는 알파와 결과가 어긋난다.
 * 시안이 `background-currentColor-8` 처럼 알파로 지정한 곳에는 이쪽을 쓴다.
 *
 * 형식이 잘못된 입력은 기본 파랑으로 떨어진다 (`buildSelectionAccentTokens` 와 같은 방어).
 */
export function withAlpha(hex: string, alpha: number): string {
  const rgb = parseHex(hex) ?? parseHex(DEFAULT_BASE);
  if (!rgb) return hex;

  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

/**
 * base 색 하나에서 Selection Control 의 accent 3종을 만든다.
 *
 * 형식이 잘못된 입력이면 기본 파랑으로 떨어진다 (`getCalendarAccent` 와 같은 방어).
 * prop 타입이 `string` 인 이상 런타임 방어가 필요하고, 색 하나 때문에 화면이
 * 죽는 것보다 기본색으로 그리는 편이 낫다.
 */
export function buildSelectionAccentTokens(
  base: string,
): SelectionAccentTokens {
  const normalized = parseHex(base);
  if (!normalized) return SELECTION_ACCENT_DEFAULT;

  const canonical = `#${normalized.map(toHex).join('')}`;

  return {
    base: canonical,
    hover: mixWithWhite(canonical, HOVER_MIX_RATIO),
    halo: mixWithWhite(canonical, HALO_MIX_RATIO),
  };
}

/**
 * DS 기본 파랑. `buildSelectionAccentTokens('#0066CC')` 과 같다.
 *
 * 모듈 레벨 상수라 accent 를 안 넘긴 호출부는 함수 호출조차 하지 않는다.
 * 값은 `{ '#0066CC', '#5C9DDE', '#C2DAF3' }` — 가이드 값과 정확히 일치한다.
 */
export const SELECTION_ACCENT_DEFAULT: SelectionAccentTokens = {
  base: DEFAULT_BASE,
  hover: mixWithWhite(DEFAULT_BASE, HOVER_MIX_RATIO),
  halo: mixWithWhite(DEFAULT_BASE, HALO_MIX_RATIO),
};
